<template>
  <modal :name="modalName" class="modal-inner modal-order-device-details" transition="pop-out" :width="modalWidth" height="auto" :scrollable="true" :adaptive="true" :clickToClose="false" @before-open="buildSections" @closed="$emit('closed')">
    <span class="close-button" @click="close"><BaseIcon icon="times-circle" class="text-white" /></span>
    <div class="modal-header">
      {{ $t("order_devices.details_title") }} <span v-if="unitName">— {{ unitName }}</span>
    </div>
    <div class="modal-body">
      <p class="text-sm text-gray-600 mb-4">
        {{ isCreateMode && !hasCreated
          ? $t("order_devices.details_create_intro", { count: sections.length })
          : $t("order_devices.details_intro", { count: sections.length }) }}
      </p>

      <div v-for="(section, i) in visibleSections" :key="section.section_key" class="pb-4 mb-4 border-b border-secondary-400">
        <div class="flex items-center justify-between mb-2">
          <div class="text-lg font-bold text-gray-800 font-serif">
            {{ $t("order_devices.section_header", { n: i + 1, total: sections.length }) }} — {{ section.type }}
            <span v-if="section.person && section.person.name" class="text-sm font-normal text-gray-600 ml-2">
              · {{ section.person.name }}
            </span>
          </div>
          <span class="rounded-full px-2 py-0.5 text-xs font-semibold" :class="sectionBadgeClass(section)">
            {{ sectionBadgeLabel(section) }}
          </span>
        </div>
        <!-- demo feedback: the person this device is being created for, as the
             order describes them - the device is created with these details -->
        <!-- Safe-4 (SEC-370): SAFECON did not confirm this device, so it was not created -->
        <p v-if="section.errors.safe4" class="text-xs text-red-600 mb-2">{{ section.errors.safe4 }}</p>
        <p v-if="personSummary(section.person)" class="text-xs italic text-gray-600 mb-2">
          {{ personSummary(section.person) }}
        </p>

        <div class="flex flex-wrap -mx-2">
          <div class="w-full px-2 py-1">
            <!-- optional, as on the manual create page: it only auto-fills the fields below -->
            <BaseSelect v-model="section.stock_order_id" :options="stockOptionsFor(section)" nameSelector="modelnumber" optionSelector="_id" :field_name="$t('alarm_create.delivery_document')" @change="populateFromStock(section)" />
          </div>
          <div class="w-full md:w-1/2 px-2 py-1" v-if="section.visible.imei_number">
            <BaseInput v-model.trim="section.imei_number" type="text" :field_name="$t('alarm_create.imei')" rules="required" />
            <p v-if="section.errors.imei_number" class="text-xs text-red-600">{{ section.errors.imei_number }}</p>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1" v-if="section.visible.phone">
            <BaseInput v-model.trim="section.phone" type="text" :field_name="$t('alarm_create.telematic_number')" rules="required" />
            <p v-if="section.errors.phone" class="text-xs text-red-600">{{ section.errors.phone }}</p>
            <!-- inventory number sits directly under the telematics number -->
            <div v-if="section.visible.inventory_number">
              <BaseInput v-model.trim="section.inventory_number" type="text" :field_name="$t('alarm_create.inventory_number')" />
              <p v-if="section.errors.inventory_number" class="text-xs text-red-600">{{ section.errors.inventory_number }}</p>
            </div>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1" v-if="section.visible.modelnumber">
            <BaseInput v-model.trim="section.modelnumber" type="text" :field_name="$t('alarm_create.serial_number')" rules="required" />
            <p v-if="section.errors.modelnumber" class="text-xs text-red-600">{{ section.errors.modelnumber }}</p>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1" v-if="section.visible.operator">
            <BaseSelect v-model="section.operator" :options="simOperators" :field_name="$t('alarm_create.operator_simcard')" rules="required" />
          </div>
    
          <!-- demo feedback layout: name + agreement in the left column,
               alarm number (+ inventory) in the right. No .trim on the name
               so spaces can be typed. -->
          <div class="w-full md:w-1/2 px-2 py-1">
            <div class="flex items-end">
              <div class="flex-grow">
                <BaseInput v-model="section.unit_name" type="text" :field_name="$t('alarm_create.product_name')" rules="required" @input="section.unit_name_applied_from = null" />
              </div>
              <button
                v-if="sections.length > 1"
                type="button"
                class="ml-1 mb-3 px-2 py-1.5 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="!(section.unit_name || '').trim()"
                :title="$t('order_devices.apply_to_all')"
                @click="applyNameToAll(i)"
              >
                <BaseIcon icon="copy" />
              </button>
            </div>
            <p v-if="section.unit_name_applied_from" class="text-xs text-green-600">
              {{ $t("order_devices.applied_from", { n: section.unit_name_applied_from }) }}
            </p>
          </div>
          <!-- always shown here, unlike the create form where it depends on
               the alarm central: support may know it at this point -->
          <div class="w-full md:w-1/2 px-2 py-1">
            <BaseInput v-model.trim="section.alarm_number" type="text" :field_name="$t('alarm_create.alarm_number')" />
          </div>
          <!-- SEC-337: IMSI number, only when the device's alarm centre is
               Securitas Sverige AB (same rule as the create and edit forms) -->
          <div class="w-full md:w-1/2 px-2 py-1" v-if="showImsiNumber(section)">
            <BaseInput v-model.trim="section.imsi_number" type="text" :field_name="$t('change_alarm_plan.imsi_number')" />
            <p v-if="section.errors.imsi_number" class="text-xs text-red-600">{{ section.errors.imsi_number }}</p>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1">
            <div class="flex items-end">
              <div class="flex-grow">
                <BaseInput v-model.trim="section.agreement_number" type="text" :field_name="$t('alarm_create.agreement_number')" @input="section.agreement_applied_from = null" />
              </div>
              <button
                v-if="sections.length > 1"
                type="button"
                class="ml-1 mb-3 px-2 py-1.5 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="!(section.agreement_number || '').trim()"
                :title="$t('order_devices.apply_to_all')"
                @click="applyAgreementToAll(i)"
              >
                <BaseIcon icon="copy" />
              </button>
            </div>
            <p v-if="section.agreement_applied_from" class="text-xs text-green-600">
              {{ $t("order_devices.applied_from", { n: section.agreement_applied_from }) }}
            </p>
          </div>

          <!-- demo feedback: external responder from the order — editable per
               device, one click copies all three values to the other devices -->
          <div class="w-full px-2 pt-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-bold text-gray-700 font-serif">{{ $t("admin_order_alarm.security_company") }}</span>
              <button
                v-if="sections.length > 1"
                type="button"
                class="px-2 py-1 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="!hasResponderValues(section)"
                :title="$t('order_devices.apply_to_all')"
                @click="applyResponderToAll(i)"
              >
                <BaseIcon icon="copy" />
              </button>
            </div>
            <div class="flex flex-wrap -mx-2">
              <!-- demo feedback: no .trim here - it strips the space the moment
                   it is typed, so multi-word names ("Securitas AB") and spaced
                   phone numbers could not be entered. Trimmed on save instead. -->
              <div class="w-full md:w-1/3 px-2 py-1">
                <BaseInput v-model="section.external_contractor.name" type="text" :field_name="$t('alarm_create.name')" @input="section.responder_applied_from = null" />
              </div>
              <div class="w-full md:w-1/3 px-2 py-1">
                <BaseInput v-model="section.external_contractor.phone" type="text" :field_name="$t('alarm_create.contact_number')" @input="section.responder_applied_from = null" />
              </div>
              <div class="w-full md:w-1/3 px-2 py-1">
                <BaseInput v-model="section.external_contractor.object_id" type="text" :field_name="$t('alarm_create.object_id')" @input="section.responder_applied_from = null" />
              </div>
            </div>
            <p v-if="section.responder_applied_from" class="text-xs text-green-600">
              {{ $t("order_devices.applied_from", { n: section.responder_applied_from }) }}
            </p>
          </div>
        </div>
      </div>

      <!-- remaining devices, collapsed -->
      <div v-if="!showAll && sections.length > INITIALLY_EXPANDED" class="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-4 py-3 cursor-pointer hover:bg-blue-100 transition-colors" @click="showAll = true">
        <span class="text-lg font-bold text-gray-800 font-serif">
          {{ $t("order_devices.collapsed_devices", { from: INITIALLY_EXPANDED + 1, to: sections.length }) }}
        </span>
        <BaseIcon icon="chevron-down" class="text-blue-500" />
      </div>

      <!-- demo feedback: creating devices here also sends their documentation,
           said again at the end of the form, just before the create button -->
      <p v-if="isCreateMode && pendingSections.length" class="mt-4 text-sm italic text-black">
        {{ $t("order_devices.auto_send_hint") }}
      </p>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix flex items-center justify-between">
        <button class="btn-blue-outline px-3 py-2" @click="close">{{ $t("order_log.cancel") }}</button>
        <span class="text-sm text-gray-600">{{ $t("order_devices.completed_counter", { done: completedCount, total: sections.length }) }}</span>
        <!-- create mode: create the devices with or without sending their
             documentation to the alarm central; other modes keep the single
             save button (they never send) -->
        <div v-if="isCreateMode && pendingSections.length" class="flex items-center">
          <button class="btn-green-outline px-3 py-2 mr-2" :disabled="saveDisabled" :class="{ 'opacity-50 cursor-not-allowed': saveDisabled }" @click="saveAll(false)">
            {{ $t("order_devices.create_button", { count: createTargets.length }) }}<BaseIcon icon="save" class="ml-1" />
          </button>
          <button class="btn-green px-3 py-2" :disabled="saveDisabled" :class="{ 'opacity-50 cursor-not-allowed': saveDisabled }" @click="saveAll(true)">
            {{ $t("order_devices.create_and_send_button", { count: createTargets.length }) }}<BaseIcon icon="envelope" class="ml-1" />
          </button>
        </div>
        <button v-else class="btn-green px-3 py-2" :disabled="saveDisabled" :class="{ 'opacity-50 cursor-not-allowed': saveDisabled }" @click="saveAll(false)">
          {{ $t("order_devices.save_all_devices") }}<BaseIcon icon="save" class="ml-1" />
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 800;
const INITIALLY_EXPANDED = 2;

// The identifier fields that must be unique across all devices.
const UNIQUE_FIELDS = ["imei_number", "modelnumber", "phone", "inventory_number", "imsi_number"];

// SEC-338: an IMEI, serial or telematics number containing a special character
// (anything but a letter or digit) is a placeholder support typed while the
// real value is unknown, so several devices may share it. A phone number
// written with ordinary formatting ("+46 70-123 45 67") is still a real number.
// Mirrors isPlaceholderIdentifier in the server's utils/alarm-identifiers.js -
// keep the two identical.
const PLACEHOLDER_FIELDS = ["imei_number", "modelnumber", "phone", "imsi_number"];
const isPlaceholderIdentifier = (field, value) => {
  if (!PLACEHOLDER_FIELDS.includes(field)) return false;
  const text = String(value == null ? "" : value).trim();
  if (!text) return false;
  if ((field === "phone" || field === "imsi_number") && /^[0-9]+$/.test(text.replace(/[+\s\-()]/g, ""))) return false;
  return /[^\p{L}\p{N}]/u.test(text);
};

import { deviceFieldVisibility, isOrderDeviceIncomplete, SIM_OPERATORS } from "@/utils/automaticDeviceCreationFields";

export default {
  name: "orderDeviceDetailsModal",

  props: {
    // "complete": sections come from existing incomplete devices (resume flow
    // in the business view). "create": sections come from pendingDevices and
    // nothing exists in the database until the footer button is clicked —
    // closing before that creates nothing.
    mode: {
      type: String,
      default: "complete",
    },
    modalName: {
      type: String,
      default: "modal-order-device-details",
    },
    // incomplete order-created devices of the business (complete mode)
    devices: {
      type: Array,
      default: () => [],
    },
    // missing order-line slots to create (create mode):
    // [{ line_key, index, device_type }]
    pendingDevices: {
      type: Array,
      default: () => [],
    },
    // the order to create devices from (create mode)
    orderId: {
      type: String,
      default: "",
    },
    // prefilled on every section, editable per device (create mode)
    agreementNumber: {
      type: String,
      default: "",
    },
    // SEC-337: the alarm centre the order's devices are created with (create
    // mode) - decides whether the IMSI field is shown
    alarmCentralId: {
      type: String,
      default: "",
    },
    // external responder from the order (demo feedback): { name, phone, object_id }
    externalResponder: {
      type: Object,
      default: () => ({}),
    },
    unitName: {
      type: String,
      default: "",
    },
    // demo feedback: a personal order describes one person per alarm. They are
    // handed out in order, so the first device created here gets the first
    // person still waiting for one.
    people: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      modalWidth: window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH,
      INITIALLY_EXPANDED,
      sections: [],
      stockOrders: [],
      alarmCentrals: [], // SEC-337: to tell which centre a section is on
      savingAll: false,
      showAll: false,
      simOperators: SIM_OPERATORS,
    };
  },

  computed: {
    isCreateMode() {
      return this.mode === "create";
    },
    // In create mode: have the database documents been created yet?
    hasCreated() {
      return this.sections.some((s) => s.alarm_id);
    },
    // Create mode: sections not yet backed by a database document.
    pendingSections() {
      return this.sections.filter((s) => !s.alarm_id);
    },
    // What the create button will create: only the pending sections whose
    // required fields are filled in. Devices are never created empty from
    // this modal — an unfilled section is simply not created yet.
    createTargets() {
      return this.pendingSections.filter((s) => this.isSectionFilled(s));
    },
    visibleSections() {
      return this.showAll ? this.sections : this.sections.slice(0, INITIALLY_EXPANDED);
    },
    completedCount() {
      return this.sections.filter((s) => s.saved || this.isSectionFilled(s)).length;
    },
    // Delivery documents already picked in some section (computed once per
    // render instead of per dropdown).
    selectedStockIds() {
      return new Set(this.sections.map((s) => s.stock_order_id).filter(Boolean));
    },
    // Only complete devices are ever saved — with none complete there is
    // nothing the button could do, so it is disabled.
    saveDisabled() {
      return this.savingAll || !this.sections.some((s) => !s.saved && this.isSectionFilled(s));
    },
  },

  methods: {
    // Sections are built when the modal opens and are never rebuilt while it
    // is open, so typed-but-unsaved values survive list refreshes (REQ-04).
    buildSections() {
      this.showAll = false;
      this.sections = this.isCreateMode ? this.buildPendingSections() : this.buildAlarmSections();
      this.fetchStockOrders();
      this.fetchAlarmCentrals();
    },

    // Complete mode: one section per existing incomplete device.
    buildAlarmSections() {
      return this.devices
        .filter((alarm) => isOrderDeviceIncomplete(alarm))
        .map((alarm) => ({
          section_key: alarm._id,
          alarm_id: alarm._id,
          type: alarm.type || "",
          unit_name: alarm.unit_name || "",
          visible: deviceFieldVisibility(alarm.type),
          stock_order_id: "",
          imei_number: alarm.imei_number || "",
          phone: alarm.phone || "",
          modelnumber: alarm.modelnumber || "",
          operator: alarm.operator || "",
          inventory_number: alarm.inventory_number || "",
          alarm_number: alarm.alarm_number || "",
          alarm_central: alarm.alarm_central || "",
          imsi_number: alarm.imsi_number || "", // SEC-337
          // inherited from the order at creation; shown pre-filled, editable
          agreement_number: alarm.agreement_number || "",
          external_contractor: this.buildResponder(alarm.external_contractor),
          agreement_applied_from: null,
          unit_name_applied_from: null,
          responder_applied_from: null,
          errors: {},
          saving: false,
          saved: false,
        }));
    },

    // Create mode: one section per missing order-line slot. No device exists
    // yet — alarm_id is assigned after createDevices().
    buildPendingSections() {
      return this.pendingDevices.map((slot, position) => {
        const person = this.people[position] || null;
        return {
        section_key: `${slot.line_key}::${slot.index}`,
        alarm_id: null,
        line_key: slot.line_key,
        slot_index: slot.index,
        type: slot.device_type || "",
        // whose alarm this is, and the product name written for them. No name is
        // suggested otherwise ("SRT405 1"): support types it, the field is required.
        person,
        unit_name: (person && person.alarms_name) || "",
        visible: deviceFieldVisibility(slot.device_type),
        stock_order_id: "",
        imei_number: "",
        phone: "",
        modelnumber: "",
        operator: "Tele2",
        inventory_number: "",
        alarm_number: "",
        alarm_central: this.alarmCentralId || "",
        imsi_number: "", // SEC-337
        agreement_number: this.agreementNumber || "",
        external_contractor: this.buildResponder(this.externalResponder),
        agreement_applied_from: null,
        unit_name_applied_from: null,
        responder_applied_from: null,
        errors: {},
        saving: false,
        saved: false,
        };
      });
    },

    // The person's details in one line, skipping what the order left empty
    personSummary(person) {
      if (!person) return "";
      const transport = {
        walking: this.$t('alarm_create.transport_walking'),
        bike: this.$t('alarm_create.transport_bike'),
        public_transport: this.$t('alarm_create.transport_public_transport'),
        car: this.$t('alarm_create.transport_car'),
      }[person.transport] || person.transport;
      return [
        person.reporting,
        person.home_address,
        transport,
      ].map((part) => String(part || "").trim()).filter(Boolean).join(" · ");
    },

    fetchAlarmCentrals() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`)
        .then((response) => {
          this.alarmCentrals = (response.data && response.data.data) || [];
        })
        .catch(() => {
          // without the list the IMSI field is simply not offered
          this.alarmCentrals = [];
        });
    },

    // SEC-337: Securitas Sverige AB only, matched by its exact name so the
    // separate "Securitas AB" centre does not show the field.
    showImsiNumber(section) {
      if (!section.alarm_central || section.alarm_central === "-") return false;
      const central = this.alarmCentrals.find((c) => c._id === section.alarm_central);
      return !!central && central.name === "Securitas Sverige AB";
    },

    fetchStockOrders() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/orders`)
        .then((response) => {
          this.stockOrders = response.data.orders || [];
        })
        .catch((error) => {
          this.handleError(error, this.$t("order_devices.stock_load_failed"));
        });
    },

    // Delivery documents of the section's device type, minus documents
    // already picked in another section.
    stockOptionsFor(section) {
      return this.stockOrders.filter(
        (order) =>
          order.type === section.type &&
          (order._id === section.stock_order_id || !this.selectedStockIds.has(order._id))
      );
    },

    // Same effective value the save payload sends (AJAX: serial is the IMEI).
    effectiveValue(section, field) {
      // SEC-337: the IMSI is only sent when its field is shown
      if (field === "imsi_number" && !this.showImsiNumber(section)) return "";
      return field === "imei_number" && /^AJAX/.test(section.type)
        ? (section.modelnumber || "").trim()
        : (section[field] || "").trim();
    },

    // Uniqueness within the form: the same value in two sections marks the
    // field on both and drops those sections from this round — the other
    // candidates still get saved. Returns the conflict-free candidates.
    markFormDuplicates(candidates) {
      const conflicted = new Set();

      UNIQUE_FIELDS.forEach((field) => {
        const sectionsByValue = new Map();
        this.sections.forEach((section, index) => {
          const value = this.effectiveValue(section, field);
          if (!value) return;
          if (isPlaceholderIdentifier(field, value)) return;
          if (!sectionsByValue.has(value)) sectionsByValue.set(value, []);
          sectionsByValue.get(value).push(index);
        });
        sectionsByValue.forEach((indexes) => {
          if (indexes.length < 2) return;
          const devices = indexes.map((i) => i + 1).join(", ");
          indexes.forEach((i) => {
            const section = this.sections[i];
            if (section.saved) return;
            conflicted.add(section);
            this.$set(section.errors, field, this.$t("order_devices.duplicate_in_modal", { devices }));
          });
        });
      });

      if (conflicted.size) {
        this.showAll = true;
        this.handleError(null, this.$t("order_devices.duplicate_in_modal_toast"));
      }
      return candidates.filter((section) => !conflicted.has(section));
    },

    // Copy one section's agreement number to every other (unsaved) section.
    // Typing in a receiving section afterwards clears its "applied" marker.
    applyAgreementToAll(sourceIndex) {
      const source = this.sections[sourceIndex];
      const value = (source.agreement_number || "").trim();
      if (!value) return;
      this.sections.forEach((section, index) => {
        if (index === sourceIndex || section.saved) return;
        section.agreement_number = value;
        section.agreement_applied_from = sourceIndex + 1;
      });
      source.agreement_applied_from = null;
    },

    // External responder trio (demo feedback): normalized shape per section.
    // Values are trimmed here (not while typing) so spaces inside a name stay.
    buildResponder(source) {
      const responder = source || {};
      return {
        name: String(responder.name || "").trim(),
        phone: String(responder.phone || "").trim(),
        object_id: String(responder.object_id || "").trim(),
      };
    },

    hasResponderValues(section) {
      const responder = section.external_contractor || {};
      return Boolean(
        (responder.name || "").trim() || (responder.phone || "").trim() || (responder.object_id || "").trim()
      );
    },

    // One click copies all three responder values to every other (unsaved)
    // section (demo feedback).
    applyResponderToAll(sourceIndex) {
      const source = this.sections[sourceIndex];
      if (!this.hasResponderValues(source)) return;
      this.sections.forEach((section, index) => {
        if (index === sourceIndex || section.saved) return;
        section.external_contractor = this.buildResponder(source.external_contractor);
        section.responder_applied_from = sourceIndex + 1;
      });
      source.responder_applied_from = null;
    },

    applyNameToAll(sourceIndex) {
      const source = this.sections[sourceIndex];
      const value = (source.unit_name || "").trim();
      if (!value) return;
      this.sections.forEach((section, index) => {
        if (index === sourceIndex || section.saved) return;
        section.unit_name = value;
        section.unit_name_applied_from = sourceIndex + 1;
      });
      source.unit_name_applied_from = null;
    },

    populateFromStock(section) {
      const order = this.stockOrders.find((o) => o._id === section.stock_order_id);
      if (!order) return;
      section.modelnumber = order.modelnumber || "";
      section.imei_number = /^AJAX/.test(section.type) ? section.modelnumber : (order.imei_number || "");
    },

    isSectionFilled(section) {
      const required = ["imei_number", "phone", "modelnumber", "operator"];
      if (!(section.unit_name || "").trim()) return false;
      return required.every((field) => !section.visible[field] || (section[field] && String(section[field]).trim()));
    },

    sectionBadgeLabel(section) {
      if (section.saved) return this.$t("order_devices.section_saved");
      if (section.stock_order_id) return this.$t("order_devices.section_autofilled");
      if (this.isSectionFilled(section)) return this.$t("order_devices.section_filled");
      return this.$t("order_devices.section_not_filled");
    },

    sectionBadgeClass(section) {
      if (section.saved || section.stock_order_id || this.isSectionFilled(section)) return "bg-green-100 text-green-800";
      return "bg-red-100 text-red-700";
    },

    async saveSection(section) {
      section.saving = true;
      section.errors = {};

      const payload = {
        imei_number: /^AJAX/.test(section.type) ? section.modelnumber : section.imei_number,
        phone: section.phone,
        modelnumber: section.modelnumber,
        operator: section.operator,
  
        ...((section.unit_name || "").trim() ? { unit_name: section.unit_name.trim() } : {}),
        inventory_number: section.inventory_number,
        alarm_number: section.alarm_number,
        ...(this.showImsiNumber(section) ? { imsi_number: section.imsi_number } : {}), // SEC-337
        agreement_number: section.agreement_number,
        // demo feedback: responder details editable per device
        external_contractor: this.buildResponder(section.external_contractor),
        order_id: section.stock_order_id || "",
      };

      try {
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/alarms/${section.alarm_id}`, payload);
        section.saved = true;
        if (section.stock_order_id) {
          // the delivery document is consumed server-side
          this.stockOrders = this.stockOrders.filter((o) => o._id !== section.stock_order_id);
        }
        this.$emit("devices-updated");
        return true;
      } catch (error) {
        // Safe-4 refused this device, so it was not saved (SEC-364 B1, C4)
        if (this.handleSafe4Rejection(error)) {
          this.showAll = true; // the failing section may be collapsed
          return false;
        }
        const data = error.response && error.response.status === 409 ? error.response.data : null;
        if (data && data.errors) {
          Object.keys(data.errors).forEach((field) => {
            const info = data.errors[field] || {};
            this.$set(section.errors, field, this.identifierConflictMessage(field, info));
          });
          this.showAll = true; // the failing section may be collapsed
          this.handleError(null, this.$t("order_devices.section_conflict", { device: `${section.type} — ${section.unit_name}` }));
        } else {
          this.handleError(error, this.$t("order_devices.section_save_failed"));
        }
        return false;
      } finally {
        section.saving = false;
      }
    },

    // Create mode: the actual database creation. One idempotent POST creates
    // exactly the given sections' slots; the returned slot → alarm mapping
    // attaches the sections to the created documents so the entered values
    // can be saved onto them. Slots whose identifiers conflict with the
    // register are NOT created — the server skips them and reports them in
    // identifier_errors, so one bad value never blocks the rest.
    async createDevices(targets, sendToCentral = false) {
      const lineByKey = new Map();
      targets.forEach((section) => {
        if (!lineByKey.has(section.line_key)) {
          lineByKey.set(section.line_key, {
            line_key: section.line_key,
            device_type: section.type,
            slots: [],
          });
        }
        // The identifiers travel with the create request so the server can
        // reject duplicates BEFORE any device exists (AJAX: serial is the IMEI).
        // The non-identifier fields the documentation prints ride along too -
        // the alarm number and the external responder (section 5) - so the
        // report the central receives right after creation carries them
        // instead of empty values.
        lineByKey.get(section.line_key).slots.push({
          index: section.slot_index,
          // the device is created with the name typed here, so it never carries
          // a made-up one - not even in the documentation sent right away
          unit_name: (section.unit_name || "").trim(),
          imei_number: /^AJAX/.test(section.type) ? section.modelnumber : section.imei_number,
          modelnumber: section.modelnumber,
          phone: section.phone,
          inventory_number: section.inventory_number,
          alarm_number: section.alarm_number,
          imsi_number: this.showImsiNumber(section) ? section.imsi_number : "", // SEC-337
          external_contractor: this.buildResponder(section.external_contractor),
        });
      });
      const lines = [...lineByKey.values()];

      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/order-alarm/${this.orderId}/create-devices`,
          { lines }
        );
        const { results, partial, devices_by_slot: devicesBySlot, identifier_errors: identifierErrors, safe4_errors: safe4Errors } = response.data;

        const idBySlot = new Map((devicesBySlot || []).map((d) => [`${d.line_key}::${d.index}`, d.alarm_id]));
        this.sections.forEach((section) => {
          if (!section.alarm_id) section.alarm_id = idBySlot.get(section.section_key) || null;
        });

        // Register conflicts: the slot was skipped — show the conflict on the
        // exact field in the exact section.
        let conflicts = false;
        this.sections.forEach((section) => {
          const fields = (identifierErrors || {})[section.section_key];
          if (!fields) return;
          conflicts = true;
          Object.keys(fields).forEach((field) => {
            const info = fields[field] || {};
            this.$set(section.errors, field, this.identifierConflictMessage(field, info));
          });
        });
        if (conflicts) {
          this.showAll = true;
          this.handleError(null, this.$t("order_devices.duplicate_in_modal_toast"));
        }

        // Safe-4 (SEC-370): a device on Safe-4 is created only when SAFECON
        // confirmed it. A refused one was NOT created - say why on its section.
        let safe4Refused = false;
        this.sections.forEach((section) => {
          const refusal = (safe4Errors || {})[section.section_key];
          if (!refusal) return;
          safe4Refused = true;
          const text = refusal.reason === "imei_required" ? this.$t("safe4_form.imei_required")
            : refusal.reason === "order_incomplete" ? this.$t("safe4_form.order_incomplete")
            : this.safe4RefusalText(refusal);
          this.$set(section.errors, "safe4", text);
        });
        if (safe4Refused) {
          this.showAll = true;
          this.handleError(null, this.$t("safe4_form.order_devices_refused"), "top-right", 10000);
        }

        // the panel emails the new devices' documentation only when the user
        // chose "create and send to alarm central"
        this.$emit("devices-created", response.data, sendToCentral);
        const createdNow = results.reduce((sum, r) => sum + r.created, 0);
        if (createdNow > 0) this.handleSuccess(this.$t("order_devices.created_success", { count: createdNow }));
        if (partial) this.handleError(null, this.$t("order_devices.partial_failure"));
      } catch (error) {
        this.handleError(error, this.$t("order_devices.create_failed"));
      }
    },

    // One save pass, both modes. Every device is validated independently:
    // a section is saved only when all its required fields are filled and its
    // identifiers are unique (in the form and in the register). Sections that
    // don't qualify are marked or simply left for the next round — they never
    // block the ones that do. The modal closes (and create mode redirects)
    // only when every device is saved.
    async saveAll(sendToCentral = false) {
      this.savingAll = true;
      try {
        this.sections.forEach((section) => {
          if (!section.saved) section.errors = {};
        });

        let candidates = this.sections.filter((s) => !s.saved && this.isSectionFilled(s));
        candidates = this.markFormDuplicates(candidates);

        if (this.isCreateMode) {
          const toCreate = candidates.filter((s) => !s.alarm_id);
          if (toCreate.length) await this.createDevices(toCreate, sendToCentral);
        }

        for (const section of candidates) {
          if (!section.alarm_id || Object.keys(section.errors).length) continue;
          await this.saveSection(section);
        }

        if (this.sections.length && this.sections.every((s) => s.saved)) {
          this.$modal.hide(this.modalName);
          if (this.isCreateMode) this.$emit("completed"); // owner redirects to the business
          else this.handleSuccess(this.$t("order_devices.all_saved"));
        }
      } finally {
        this.savingAll = false;
      }
    },

    // Cancel/close never writes, in either mode — devices are only created
    // and saved through the footer button. Incomplete devices stay flagged
    // in the business view (yellow banner), so entry can always be resumed.
    close() {
      this.$modal.hide(this.modalName);
    },
  },
};
</script>
